import { useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { startOfDay, subDays, parseISO, endOfDay } from 'date-fns';
import { Session, SessionStatus } from '@/types/session';
import { DatePreset, SessionFilters, DEFAULT_FILTERS } from '@/types/filters';

export function useSessionFilters(sessions: Session[]) {
  const [searchParams, setSearchParams] = useSearchParams();

  const filters: SessionFilters = useMemo(
    () => ({
      datePreset:
        (searchParams.get('datePreset') as DatePreset) ||
        DEFAULT_FILTERS.datePreset,
      dateFrom: searchParams.get('dateFrom') || null,
      dateTo: searchParams.get('dateTo') || null,
      ward: searchParams.get('ward') || '',
      status:
        (searchParams.get('status') as SessionStatus | 'all') || 'all',
    }),
    [searchParams],
  );

  const effectiveDateRange = useMemo(() => {
    const now = new Date();
    if (filters.datePreset === 'today') {
      return { from: startOfDay(now), to: endOfDay(now) };
    }
    if (filters.datePreset === 'last7') {
      return { from: startOfDay(subDays(now, 7)), to: endOfDay(now) };
    }
    if (filters.datePreset === 'last30') {
      return { from: startOfDay(subDays(now, 30)), to: endOfDay(now) };
    }
    // 'all' preset or custom dates
    return {
      from: filters.dateFrom ? startOfDay(parseISO(filters.dateFrom)) : null,
      to: filters.dateTo ? endOfDay(parseISO(filters.dateTo)) : null,
    };
  }, [filters.datePreset, filters.dateFrom, filters.dateTo]);

  const filteredSessions = useMemo(() => {
    return sessions.filter((session) => {
      const sessionDate = session.sessionDate;

      if (effectiveDateRange.from && sessionDate < effectiveDateRange.from)
        return false;
      if (effectiveDateRange.to && sessionDate > effectiveDateRange.to)
        return false;

      if (filters.ward && session.wardName !== filters.ward) return false;

      if (filters.status !== 'all' && session.status !== filters.status)
        return false;

      return true;
    });
  }, [sessions, effectiveDateRange, filters.ward, filters.status]);

  const availableWards = useMemo(() => {
    const wards = new Set(sessions.map((s) => s.wardName));
    return Array.from(wards).sort();
  }, [sessions]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.datePreset !== 'all') count++;
    if (filters.dateFrom || filters.dateTo) count++;
    if (filters.ward) count++;
    if (filters.status !== 'all') count++;
    return count;
  }, [filters]);

  const setFilter = useCallback(
    <K extends keyof SessionFilters>(key: K, value: SessionFilters[K]) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (
            value === null ||
            value === '' ||
            value === DEFAULT_FILTERS[key]
          ) {
            next.delete(key);
          } else {
            next.set(key, String(value));
          }
          // When selecting a preset, clear custom date fields
          if (key === 'datePreset' && value !== 'all') {
            next.delete('dateFrom');
            next.delete('dateTo');
          }
          // When setting custom dates, clear preset
          if (key === 'dateFrom' || key === 'dateTo') {
            next.delete('datePreset');
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const clearFilters = useCallback(() => {
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  return {
    filters,
    filteredSessions,
    availableWards,
    activeFilterCount,
    setFilter,
    clearFilters,
  };
}
